"use client"

import { useState, Fragment, useEffect } from "react"
import { Dialog, Transition } from "@headlessui/react"
import {
  X,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Clock,
  Check,
  Shield,
  AlertCircle,
  Tag,
  Loader2,
  Building,
  Server,
  Globe,
  Percent,
  LayoutGrid,
} from "lucide-react"

import { SearchableSelect, InputCustom } from "../../components"
import providersService from "../../services/providersService"
import adAccountsService from "../../services/adAccountsService"
import toast from "react-hot-toast"
import { useCurrentUser } from "../../utils/context/UserProvider"

const CATEGORIES = [
  { id: 1, name: "Select Category", value: "" },
  { id: 2, name: "E-commerce", value: "ecommerce" },
  { id: 3, name: "Finance", value: "finance" },
  { id: 4, name: "Education", value: "education" },
  { id: 5, name: "Entertainment", value: "entertainment" },
  { id: 6, name: "Health & Fitness", value: "health" },
  { id: 7, name: "Technology", value: "technology" },
  { id: 8, name: "Travel", value: "travel" },
  { id: 9, name: "Food & Beverage", value: "food" },
  { id: 10, name: "Real Estate", value: "realestate" },
  { id: 11, name: "Other (custom)", value: "other" },
]

const KNOWN_CATEGORY_VALUES = CATEGORIES.map((c) => c.value).filter(
  (v) => v && v !== "other"
)

// Status Option Card Component
const StatusOptionCard = ({
  id,
  title,
  description,
  icon,
  color,
  selected,
  onClick,
}) => {
  const colorClasses = {
    green: {
      selected:
        "border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-500",
      icon: "bg-green-100 dark:bg-green-800/40 text-green-600 dark:text-green-400",
      text: "text-green-700 dark:text-green-400",
      dot: "bg-green-500",
    },
    yellow: {
      selected:
        "border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-500",
      icon: "bg-yellow-100 dark:bg-yellow-800/40 text-yellow-600 dark:text-yellow-400",
      text: "text-yellow-700 dark:text-yellow-400",
      dot: "bg-yellow-500",
    },
    orange: {
      selected:
        "border-orange-500 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-500",
      icon: "bg-orange-100 dark:bg-orange-800/40 text-orange-600 dark:text-orange-400",
      text: "text-orange-700 dark:text-orange-400",
      dot: "bg-orange-500",
    },
    red: {
      selected:
        "border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-500",
      icon: "bg-red-100 dark:bg-red-800/40 text-red-600 dark:text-red-400",
      text: "text-red-700 dark:text-red-400",
      dot: "bg-red-500",
    },
    blue: {
      selected:
        "border-blue-500 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-500",
      icon: "bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-400",
      text: "text-blue-700 dark:text-blue-400",
      dot: "bg-blue-500",
    },
  };

  // fallback to "yellow" if color is missing/invalid
  const palette = colorClasses[color] || colorClasses.yellow;

  return (
    <div
      className={`relative overflow-hidden rounded-xl p-3 cursor-pointer transition-all border ${selected
        ? palette.selected
        : "bg-white border-neutral-200 dark:bg-neutral-900/70 dark:border-neutral-800 hover:border-blue-300 dark:hover:border-blue-600"
        }`}
      onClick={() => onClick(id)}
    >
      <div className="flex items-center gap-3">
        <div
          className={`p-2 rounded-full ${selected
            ? palette.icon
            : "bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"
            }`}
        >
          {icon}
        </div>

        <div className="flex-1">
          <h3
            className={`text-sm font-medium ${selected
              ? palette.text
              : "text-neutral-700 dark:text-neutral-300"
              }`}
          >
            {title}
          </h3>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            {description}
          </p>
        </div>

        <div
          className={`w-4 h-4 rounded-full flex items-center justify-center ${selected
            ? palette.dot
            : "border border-neutral-300 dark:border-neutral-600"
            }`}
        >
          {selected && <Check size={10} className="text-white" />}
        </div>
      </div>
    </div>
  );
};


const ChangeStatusModal = ({ isOpen, onClose, account, setDone, done }) => {

  const { currentUser } = useCurrentUser()
  const isSuperAdminRole =
    currentUser?.role?.name?.toLowerCase() === 'superadmin';

  const [selectedStatus, setSelectedStatus] = useState("")
  const [reason, setReason] = useState("")
  const [notes, setNotes] = useState("")
  const [accountName, setAccountName] = useState("")
  const [accountAlias, setAccountAlias] = useState("")
  const [category, setCategory] = useState("")
  const [customCategory, setCustomCategory] = useState("")

  const [adAccountId, setAdAccountId] = useState("")
  const [parentBmId, setParentBmId] = useState("")
  const [provider, setProvider] = useState("")
  const [feePercentage, setFeePercentage] = useState("")
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState(null)
  const [providerOptions, setProviderOptions] = useState([])

  useEffect(() => {
    if (isOpen && account) {
      fetchProviders()
      // Initialize form with account data when modal opens
      setSelectedStatus(account?.status || "")
      setReason(account?.reason || "")
      setNotes(account?.notes || "")
      setAdAccountId(account?.adAccountId || "")
      setParentBmId(account?.parentBmId || "")
      setProvider(account?.provider || "")
      setAccountName(account?.account_name || "")
      setAccountAlias(account?.account_alias || "")
      const initialCategory = account?.category || ""
      if (initialCategory && !KNOWN_CATEGORY_VALUES.includes(initialCategory)) {
        setCategory("other")
        setCustomCategory(initialCategory)
      } else {
        setCategory(initialCategory)
        setCustomCategory("")
      }
      setFeePercentage(
        account?.feePercentage !== undefined && account?.feePercentage !== null
          ? String(account.feePercentage)
          : ""
      )
      setIsProcessing(false)
      setIsComplete(false)
      setError(null)
    }
  }, [isOpen, account])

  // Status options
  const statusOptions = [
    {
      id: "active",
      title: "Active",
      description: "Account is fully operational and can run ads.",
      icon: <CheckCircle size={16} />,
      color: "green",
    },
    {
      id: "pending",
      title: "Pending",
      description: "Account is awaiting approval or verification.",
      icon: <Clock size={16} />,
      color: "yellow",
    },
    {
      id: "suspended",
      title: "Suspended",
      description: "Account is temporarily disabled. Action is required to restore access.",
      icon: <AlertTriangle size={16} />,
      color: "orange",
    },
    {
      id: "reinstated",
      title: "Reinstated",
      description: "Account was previously suspended but is now restored and under review.",
      icon: <CheckCircle size={16} />,
      color: "green",
    },
    {
      id: "banned",
      title: "Banned",
      description: "Account is permanently disabled and cannot be used.",
      icon: <XCircle size={16} />,
      color: "red",
    },
  ];




  // Get status badge color
  const getStatusBadgeColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60"
      case "pending":
        return "bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-100 dark:border-yellow-800/60"
      case "suspended":
        return "bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border border-orange-100 dark:border-orange-800/60"
      case "reinstated":
        return "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-100 dark:border-green-800/60"
      case "banned":
        return "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-100 dark:border-red-800/60"
      default:
        return "bg-neutral-50 text-neutral-700 dark:bg-neutral-900/40 dark:text-neutral-300 border border-neutral-100 dark:border-neutral-800/70"
    }
  }

  // Get status dot color
  const getStatusDotClass = (status) => {
    switch (status) {
      case "active":
        return "bg-green-500"
      case "pending":
        return "bg-yellow-500"
      case "suspended":
        return "bg-orange-500"
      case "reinstated":
        return "bg-green-500"
      case "banned":
        return "bg-red-500"
      default:
        return "bg-neutral-500"
    }
  }

  const fetchProviders = async () => {
    try {

      const response = await providersService.searchAll()
      const responseData = response.data

      if (responseData && responseData.statusCode === 200 && Array.isArray(responseData.data)) {
        const dataClients = responseData.data
          .filter((item) => item && item._id) // Filter out invalid items
          .map((item) => ({
            id: item._id,
            name: item.name || "Unknown Provider",
            value: item._id,
          }))
        setProviderOptions(dataClients)
      } else {
        toast.error("Failed to load providers")
      }

    } catch (error) {
      console.error("Failed to fetch providers:", error)
      toast.error(error.response?.data?.message || "Failed to load providers")
    }
  }

  // Reset state when modal opens
  const resetState = () => {
    setSelectedStatus(account?.status?.toLowerCase() || "")
    setReason(account?.reason || "")
    setNotes(account?.notes || "")
    setAdAccountId(account?.adAccountId || "")
    setParentBmId(account?.parentBmId || "")
    setProvider(account?.provider || "")
    setAccountName(account?.account_name || "")
    const resetCategory = account?.category || ""
    if (resetCategory && !KNOWN_CATEGORY_VALUES.includes(resetCategory)) {
      setCategory("other")
      setCustomCategory(resetCategory)
    } else {
      setCategory(resetCategory)
      setCustomCategory("")
    }
    setFeePercentage(
      account?.feePercentage !== undefined && account?.feePercentage !== null
        ? String(account.feePercentage)
        : ""
    )

    setIsProcessing(false)
    setIsComplete(false)
    setError(null)
  }

  // Handle submit
  const handleSubmit = async () => {
    if (!selectedStatus) {
      setError("Please select a status")
      return
    }

    if (feePercentage !== "") {
      const feeNum = Number(feePercentage)
      if (!Number.isFinite(feeNum) || feeNum < 0 || feeNum > 100) {
        setError("Fee percentage must be a number between 0 and 100")
        return
      }
    }

    if (category === "other" && !customCategory.trim()) {
      setError("Please enter a custom category")
      return
    }

    const effectiveCategory =
      category === "other" ? customCategory.trim() : category

    try {
      setIsProcessing(true)

      const apiData = {
        selectedStatus,
        reason,
        notes,
        adAccountId,
        parentBmId,
        provider,
        accountName,
        accountAlias,
        category: effectiveCategory,
        feePercentage: feePercentage === "" ? 0 : Number(feePercentage),
      }

      const response = await adAccountsService.changeStatus(account._id, apiData)

      if (response.data && response.data.statusCode === 200) {
        setIsComplete(true)
        toast.success("Ad Account status updated successfully")

        // Wait a moment before closing the modal to show completion state
        setTimeout(() => {
          resetState()
          setDone(!done)
          onClose()
        }, 1500)
      } else {
        throw new Error(response.data?.message || "Failed to update account status")
      }
    } catch (err) {
      console.error("Error updating account status:", err)
      setError(err.response?.data?.message || err.message || "An error occurred while updating the account")
      setIsProcessing(false)
    }
  }

  // Check if form is valid
  const isFormValid = () => {
    return selectedStatus !== ""
  }

  // Render processing state
  const renderProcessingState = () => {
    return (
      <div className="text-center py-8">
        <Loader2 size={40} className="mx-auto mb-4 animate-spin text-neutral-800 dark:text-neutral-200" />
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">Updating Account</h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-xs mx-auto">
          Please wait while we update the account settings...
        </p>
        <div className="w-full max-w-xs mx-auto h-1.5 bg-neutral-200 dark:bg-neutral-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-yellow-400 "
            style={{
              width: "100%",
              animation: "progress 2s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    )
  }

  // Render completion state
  const renderCompletionState = () => {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
          <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
        </div>
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          Account Updated Successfully!
        </h3>
        <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-xs mx-auto">
          The account has been updated with the new settings.
        </p>
        <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/30 mb-4 mx-auto max-w-xs">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={16} className="text-green-600 dark:text-green-400" />
            <span className="text-sm font-medium text-green-700 dark:text-green-400 text-left">
              Changes have been logged
            </span>
          </div>
          <div className="text-sm text-green-600 dark:text-green-300 text-left">
            A notification has been sent to the account owner
          </div>
        </div>
      </div>
    )
  }

  // Render error state
  const renderErrorState = () => {
    return (
      <div className="text-center py-8">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <XCircle size={32} className="text-red-600 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-medium text-neutral-800 dark:text-neutral-200 mb-2">Update Failed</h3>
        <p className="text-sm text-red-600 dark:text-red-400 mb-6">{error}</p>
        <div className="flex flex-col gap-3 justify-center max-w-xs mx-auto">
          <button
            onClick={() => setError(null)}
            className="px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-sm font-medium"
          >
            Try Again
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2.5 border border-neutral-300 dark:bg-neutral-800 hover:border-neutral-400 dark:hover:border-neutral-600 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 hover:bg-neutral-50 dark:hover:bg-neutral-700 text-neutral-800 dark:text-neutral-200 rounded-xl transition-colors text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    )
  }

  // Render main form
  const renderForm = () => {
    return (
      <div className="space-y-6">
        {/* Account info */}
        <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-900/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-800/40 text-blue-600 dark:text-blue-400 rounded-full">
              <Shield size={16} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200">{account?.account_name}</h3>
              {account?.account_id && (
                <p className="text-xs text-neutral-500 dark:text-neutral-400">ID: {account?.account_id}</p>
              )}
              <div className="flex items-center mt-1">
                <span className="text-xs text-neutral-500 dark:text-neutral-400 mr-2">Current Status:</span>
                <span
                  className={`inline-flex capitalize items-center px-2 py-0.5 rounded-full text-xs font-[400] ${getStatusBadgeColor(account?.status)}`}
                >
                  <span
                    className={`w-1.5 h-1.5 mr-1 rounded-full ${getStatusDotClass(account?.status)}`}
                  ></span>
                  {account?.status}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Status options */}
        <div>
          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Select New Status
          </label>
          <div className=" grid lg:grid-cols-2 gap-3 ">
            {statusOptions.map((option) => (
              <StatusOptionCard
                key={option.id}
                id={option.id}
                title={option.title}
                description={option.description}
                icon={option.icon}
                color={option.color}
                selected={selectedStatus === option.id}
                onClick={setSelectedStatus}
              />
            ))}
          </div>
        </div>

        {/* Warning for certain status changes */}
        {(selectedStatus === "suspended" || selectedStatus === "banned") && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 rounded-xl flex items-start gap-3">
            <AlertCircle size={20} className="text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-medium text-red-700 dark:text-red-400">Warning</h4>
              <p className="text-xs text-red-600 dark:text-red-300 mt-1">
                {selectedStatus === "suspended"
                  ? "Suspending this account will temporarily prevent it from running ads. The account owner will be notified."
                  : "Banning this account will permanently prevent it from running ads. This action cannot be undone."}
              </p>
            </div>
          </div>
        )}

        <div className=" grid lg:grid-cols-2 gap-3 ">







          <div>

            <InputCustom
              label="Account Name"
              placeholder="Enter account name"
              value={accountName}
              onChange={(value) => setAccountName(value)}
              icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
            />
          </div>

          <div>

            <InputCustom
              label="Account Alias (Optional)"
              placeholder="Enter account alias"
              value={accountAlias}
              onChange={(value) => setAccountAlias(value)}
              icon={<Globe size={16} className="text-neutral-500 dark:text-neutral-400" />}
            />
          </div>

          {/* Category */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
              Category
            </label>
            <SearchableSelect
              options={CATEGORIES}
              value={category}
              onChange={setCategory}
              placeholder="Select category"
              icon={<LayoutGrid size={16} />}
            />
            {category === "other" && (
              <div className="mt-3">
                <InputCustom
                  label="Custom Category"
                  placeholder="Enter your custom category"
                  value={customCategory}
                  onChange={(value) => setCustomCategory(value)}
                  icon={<LayoutGrid size={16} className="text-neutral-500 dark:text-neutral-400" />}
                />
              </div>
            )}
          </div>

          {/* Fee percentage */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
              Fee Percentage (%)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Percent size={16} className="text-neutral-500 dark:text-neutral-400" />
              </div>
              <input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={feePercentage}
                onChange={(e) => setFeePercentage(e.target.value)}
                placeholder="0.00"
                className="w-full h-10 pl-10 pr-3 text-sm border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Applied on top-ups, withdrawals and transfers for this ad account.
            </p>
          </div>

          {/* Reason for change */}
          {/* <div>
          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Reason for Change
          </label>
          <SearchableSelect
            options={reasonOptions}
            value={reason}
            onChange={setReason}
            placeholder="Select a reason"
            icon={<Activity size={16} />}
          />
        </div> */}

          {/* Notes */}


          {/* Provider */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">Provider</label>
            <SearchableSelect
              options={providerOptions}
              value={provider}
              onChange={setProvider}
              placeholder="Select a provider"
              icon={<Server size={16} />}
            />
          </div>

          {/* Ad Account ID */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
              Ad Account ID (Obligatory)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Tag size={16} className="text-neutral-500 dark:text-neutral-400" />
              </div>
              <input
                value={adAccountId}
                disabled={isSuperAdminRole ? false : account?.adAccountId ? true : false}
                onChange={(e) => setAdAccountId(e.target.value)}
                placeholder="Enter ad account ID"
                className={`w-full ${isSuperAdminRole ? '' : account?.adAccountId ? 'cursor-not-allowed' : ''} h-10 pl-10 pr-3 text-sm border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500`}
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              Enter the platform-specific ad account ID
            </p>
          </div>

          {/* Parent BM ID */}
          <div>
            <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
              Parent BM ID (Optional)
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Building size={16} className="text-neutral-500 dark:text-neutral-400" />
              </div>
              <input
                value={parentBmId}
                onChange={(e) => setParentBmId(e.target.value)}
                placeholder="Enter parent business manager ID"
                className="w-full h-10 pl-10 pr-3 text-sm border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
              For Meta accounts, enter the parent business manager ID
            </p>
          </div>


        </div>
        <div>
          <label className="block mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-200">
            Additional Notes (Optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional information about this change..."
            rows={3}
            className="w-full border p-3 text-sm border-neutral-200 dark:bg-neutral-800 rounded-xl placeholder-neutral-500 bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    )
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-xl bg-white dark:bg-neutral-950 shadow-2xl transition-all border border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                {/* Header */}
                <div className="relative p-6 border-b border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30">
                  <div className="flex items-center justify-between">
                    <Dialog.Title className="text-xl font-medium text-neutral-900 dark:text-white">
                      Update Account Status
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="p-1.5 rounded-full text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800 transition-colors"
                      type="button"
                      aria-label="Close"
                    >
                      <X size={20} />
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[70vh] overflow-y-auto">
                  {isProcessing
                    ? renderProcessingState()
                    : isComplete
                      ? renderCompletionState()
                      : error
                        ? renderErrorState()
                        : renderForm()}
                </div>

                {/* Footer */}
                {!isProcessing && !isComplete && !error && (
                  <div className="border-t border-neutral-200 dark:border-neutral-900 dark:bg-neutral-900/30 p-6 flex justify-between">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-5 h-10 border border-neutral-200 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-900/30 text-neutral-800 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors text-sm font-medium flex items-center gap-2"
                    >
                      <X size={16} />
                      Cancel
                    </button>

                    <button
                      type="button"
                      onClick={handleSubmit}
                      disabled={!isFormValid()}
                      className="px-5 h-10 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      Update Account
                      <Check size={16} />
                    </button>
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}

export default ChangeStatusModal
